import React, { useEffect, useMemo } from 'react';
import { Box, Typography, Stack, CircularProgress } from '@mui/material';
import { PageContainer } from '../components/Container/PageContainer';
import { useThreadsState } from '../contexts/ThreadsContext';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  TetrisCanvas,
  GameControls,
  GameStats,
  useTetrisGame,
  useKeyboardControls,
} from '../components/LetterTetris';
import { filterLongThreads, selectRandomThread } from '../utils/tetris-utils';

const WordTetris = () => {
  const { lifetimeThreadsList, isLifetimeReady, isLifetimeLoading } = useThreadsState();
  const isMobile = useIsMobile('md');

  // Auto-select random thread
  const selectedThread = useMemo(() => {
    const eligible = filterLongThreads(lifetimeThreadsList, 30);
    return selectRandomThread(eligible);
  }, [lifetimeThreadsList]);

  const {
    gameState,
    startGame,
    pauseGame,
    resumeGame,
    resetGame,
    moveLeft,
    moveRight,
    moveDown,
    rotate,
    hardDrop,
  } = useTetrisGame(selectedThread?.text);

  // Auto-start when ready
  useEffect(() => {
    if (selectedThread && gameState.status === 'idle') {
      const timer = setTimeout(() => startGame(), 500);
      return () => clearTimeout(timer);
    }
  }, [selectedThread, gameState.status, startGame]);

  // Keyboard controls
  useKeyboardControls({
    onLeft: moveLeft,
    onRight: moveRight,
    onDown: moveDown,
    onRotate: rotate,
    onDrop: hardDrop,
    onPause: () => {
      if (gameState.status === 'playing') pauseGame();
      else if (gameState.status === 'paused') resumeGame();
    },
    enabled: gameState.status === 'playing' || gameState.status === 'paused',
  });

  // Loading state
  if (isLifetimeLoading || !isLifetimeReady) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  // No eligible threads
  if (!selectedThread) {
    return (
      <PageContainer>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No threads available for the game
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Write some threads first!
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Word Tetris
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Clear lines to reveal words from your thread
        </Typography>
      </Box>

      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={2}
        alignItems="flex-start"
      >
        <Box>
          <TetrisCanvas gameState={gameState} />
          <GameControls
            status={gameState.status}
            onStart={startGame}
            onPause={pauseGame}
            onResume={resumeGame}
            onReset={resetGame}
            onMoveLeft={moveLeft}
            onMoveRight={moveRight}
            onMoveDown={moveDown}
            onRotate={rotate}
            onHardDrop={hardDrop}
            showMobile={isMobile}
          />
        </Box>

        <Box sx={{ minWidth: 180 }}>
          <GameStats
            score={gameState.score}
            level={gameState.level}
            lines={gameState.lines}
            nextPiece={gameState.nextPiece}
          />

          {gameState.clearedWords.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                Cleared Words
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                {gameState.clearedWords.slice(-20).join(' ')}
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </PageContainer>
  );
};

export default WordTetris;
